import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./Login.module.css";
import { CustomTextInput } from "../../components";
import { useAuth } from "../../hooks/useAuth";
import { AxiosInstance } from "../../services/Api";
import shape1 from "../../assets/images/forgot-password.png";
import logo1 from "../../assets/images/logo_1.png";

const ChangePassword = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Password validation regex patterns
  const passwordPatterns = {
    minLength: /.{6,}/,
    uppercase: /[A-Z]/,
    lowercase: /[a-z]/,
    number: /[0-9]/,
    specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
  };

  // Validate individual field
  const validateField = useCallback(
    (name, value) => {
      switch (name) {
        case "newPassword":
          if (!value) return "New password is required";
          if (!passwordPatterns.minLength.test(value)) {
            return "Password must be at least 6 characters";
          }
          if (!passwordPatterns.uppercase.test(value)) {
            return "Password must contain at least 1 uppercase letter";
          }
          if (!passwordPatterns.lowercase.test(value)) {
            return "Password must contain at least 1 lowercase letter";
          }
          if (!passwordPatterns.number.test(value)) {
            return "Password must contain at least 1 number";
          }
          if (!passwordPatterns.specialChar.test(value)) {
            return "Password must contain at least 1 special character";
          }
          return "";

        case "confirmPassword":
          if (!value) return "Please confirm your new password";
          if (value !== formData.newPassword) {
            return "Passwords do not match";
          }
          return "";

        default:
          return "";
      }
    },
    [formData.newPassword],
  );

  // Handle input change
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear API error when user modifies form
      if (apiError) {
        setApiError("");
      }

      // Clear error when user starts typing
      if (touched[name]) {
        const error = validateField(name, value);
        setErrors((prev) => ({
          ...prev,
          [name]: error,
        }));

        // Re-validate confirm password if new password changes
        if (name === "newPassword" && formData.confirmPassword) {
          const confirmError = validateField(
            "confirmPassword",
            formData.confirmPassword,
          );
          setErrors((prev) => ({
            ...prev,
            confirmPassword:
              value !== formData.confirmPassword
                ? "Passwords do not match"
                : confirmError,
          }));
        }
      }
    },
    [touched, validateField, formData.confirmPassword, apiError],
  );

  // Handle blur event
  const handleBlur = useCallback(
    (e) => {
      const { name, value } = e.target;
      setTouched((prev) => ({
        ...prev,
        [name]: true,
      }));

      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    },
    [validateField],
  );

  // Get field error
  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : "";
  };

  // Check if form is valid
  const isFormValid =
    formData.newPassword &&
    formData.confirmPassword &&
    !validateField("newPassword", formData.newPassword) &&
    !validateField("confirmPassword", formData.confirmPassword);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    // Validate all fields
    const newPasswordError = validateField("newPassword", formData.newPassword);
    const confirmPasswordError = validateField(
      "confirmPassword",
      formData.confirmPassword,
    );

    setTouched({
      newPassword: true,
      confirmPassword: true,
    });

    setErrors({
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError,
    });

    if (newPasswordError || confirmPasswordError) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await AxiosInstance.post("/login/changePassword", {
        email_id: decodeURIComponent(email),
        password: formData.newPassword,
      });

      if (response.data?.status) {
        setShowSuccessModal(true);
      } else {
        throw new Error(response.data?.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setApiError(
        error.response?.data?.message ||
          error.message ||
          "Failed to change password. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal OK
  const handleModalOk = () => {
    setShowSuccessModal(false);
    navigate("/login");
  };

  // Navigate to home
  // const handleHomeClick = () => {
  //   navigate("/");
  // };

  // Show loader while checking auth or page loading
  if (isLoading || authLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.loader}></div>
      </div>
    );
  }

  return (
    <div className={styles.mainPageWrapper}>
      <div className={styles.logSection}>
        <div className={styles.container}>
          <div className={styles.loginBox}>
            {/* Left Column */}
            <div className={styles.leftColumn}>
              <div className={styles.brandingContent}>
                <img
                  src={shape1}
                  alt="Education Illustration"
                  className={styles.illustrationImage}
                />
                <div className={styles.logoContainer}>
                  <img src={logo1} alt="iFillip Logo" className={styles.logo} />
                </div>
                <p className={styles.tagline}>
                  An omni-channel school management and learning augmentation
                  platform
                </p>
                <a
                  href="https://bharatclouds.com/"
                  className={styles.companyName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Product of Bharat Clouds Pvt Ltd
                </a>
              </div>
            </div>

            {/* Right Column */}
            <div className={styles.rightColumn}>
              <h2 className={styles.loginTitle}>Change password</h2>

              {/* API Error Message */}
              {apiError && (
                <div className={styles.apiError}>
                  <span className={styles.errorIcon}>⚠</span>
                  {apiError}
                </div>
              )}

              <form
                name="change_password"
                id="change_password"
                onSubmit={handleSubmit}
                className={styles.loginForm}
                noValidate
              >
                {/* New Password */}
                <CustomTextInput
                  type="password"
                  name="newPassword"
                  placeholder="Enter new password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={getFieldError("newPassword")}
                  maxLength={50}
                  autoComplete="new-password"
                  required
                  disabled={isSubmitting}
                />

                {/* Confirm Password */}
                <CustomTextInput
                  type="password"
                  name="confirmPassword"
                  placeholder="Enter Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={getFieldError("confirmPassword")}
                  maxLength={50}
                  autoComplete="new-password"
                  required
                  disabled={isSubmitting}
                />

                {/* Buttons */}
                <div className={styles.buttonGroup}>
                  <button
                    type="submit"
                    className={styles.homeButton}
                    disabled={!isFormValid || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className={styles.buttonLoader}></span>
                        Saving...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </button>
                  {/* <button
                    type="button"
                    className={styles.loginButton}
                    onClick={handleHomeClick}
                    disabled={isSubmitting}
                  >
                    Home
                  </button> */}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bootstrap Success Modal */}
      {showSuccessModal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="successModalLabel"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content" style={{ borderRadius: "8px" }}>
                <div
                  className="modal-body text-center"
                  style={{ padding: "40px 30px" }}
                >
                  {/* Success Checkmark Icon */}
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      border: "3px solid #c8e6c9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 25px",
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#4caf50"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>

                  {/* Title */}
                  <h4
                    style={{
                      fontFamily: "var(--font-family-primary)",
                      fontWeight: "var(--font-weight-bold)",
                      fontSize: "24px",
                      color: "var(--text-primary)",
                      marginBottom: "12px",
                    }}
                  >
                    Password Changed!
                  </h4>

                  {/* Description */}
                  <p
                    style={{
                      fontFamily: "var(--font-family-primary)",
                      fontWeight: "var(--font-weight-regular)",
                      fontSize: "var(--font-size-sm)",
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                      marginBottom: "25px",
                      padding: "0 20px",
                    }}
                  >
                    Your password has been changed successfully. Please login
                    with your new password.
                  </p>

                  {/* OK Button */}
                  <button
                    type="button"
                    className="btn"
                    onClick={handleModalOk}
                    style={{
                      backgroundColor: "#5bc0de",
                      color: "var(--color-white)",
                      fontFamily: "var(--font-family-primary)",
                      fontWeight: "var(--font-weight-semibold)",
                      fontSize: "var(--font-size-sm)",
                      padding: "10px 40px",
                      border: "none",
                      borderRadius: "var(--border-radius-sm)",
                      cursor: "pointer",
                      transition: "var(--transition-fast)",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#46b8da")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#5bc0de")
                    }
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangePassword;
